import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { 
  HiX, 
  HiArrowRight, 
  HiArrowLeft,
  HiLightBulb,
  HiFolder,
  HiClipboardList,
  HiChat,
  HiCog,
  HiCheckCircle,
  HiSparkles,
  HiUserGroup,
  HiDocumentText
} from 'react-icons/hi'

const TOUR_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to Salt & Straw Project Tracker!',
    content: 'We\'re excited to help you manage your projects more efficiently. Let\'s take a quick tour to get you started.',
    icon: HiSparkles,
    highlight: null,
  },
  {
    id: 'dashboard',
    title: 'Your Dashboard',
    content: 'This is your home base. See an overview of all your projects, tasks, upcoming deadlines, and reminders at a glance.',
    icon: HiLightBulb,
    highlight: '[data-tour="dashboard"]',
  },
  {
    id: 'create-project',
    title: 'Creating a New Project',
    content: 'Click the "New Project" button to create your first project. You can set a name, description, priority, and due date.',
    icon: HiFolder,
    highlight: '[data-tour="new-project"]',
  },
  {
    id: 'sidebar',
    title: 'Navigation Sidebar',
    content: 'Use the sidebar to navigate between Dashboard, My Tasks, Projects, Departments, and Transcripts.',
    icon: HiClipboardList,
    highlight: '[data-tour="sidebar"]',
  },
  {
    id: 'projects',
    title: 'Your Projects',
    content: 'Projects contain boards (like Kanban boards) where you can organize tasks into columns like "To Do", "In Progress", and "Done".',
    icon: HiFolder,
    highlight: '[data-tour="projects-section"]',
  },
  {
    id: 'tasks',
    title: 'Managing Tasks',
    content: 'Within each project, create tasks, assign them to team members, set priorities and due dates, and track progress.',
    icon: HiClipboardList,
    highlight: null,
  },
  {
    id: 'ai-assistant',
    title: 'HAL 9000',
    content: 'Click the chat icon to open HAL 9000. Ask it to create tasks, get summaries, search info, or help plan your work. It\'s very... eager to help.',
    icon: HiChat,
    highlight: '[data-tour="ai-chat"]',
  },
  {
    id: 'collaboration',
    title: 'Team Collaboration',
    content: 'Add team members to projects, assign tasks, and get notified about updates. Everyone stays in sync!',
    icon: HiUserGroup,
    highlight: null,
  },
  {
    id: 'settings',
    title: 'Customize Your Experience',
    content: 'Visit Settings to update your profile, notification preferences, and appearance including dark mode!',
    icon: HiCog,
    highlight: '[data-tour="settings"]',
  },
  {
    id: 'complete',
    title: 'You\'re All Set! 🚀',
    content: 'You now know the basics. Create your first project and start tracking! HAL 9000 and the help button (?) are always here if you need guidance.',
    icon: HiCheckCircle,
    highlight: null,
  },
]

function SpotlightOverlay({ targetRect }) {
  if (!targetRect) return null

  const padding = 8
  const radius = 12
  const x = targetRect.left - padding
  const y = targetRect.top - padding
  const w = targetRect.width + padding * 2
  const h = targetRect.height + padding * 2

  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 1 }}>
      <defs>
        <mask id="spotlight-mask">
          <rect width="100%" height="100%" fill="white" />
          <rect x={x} y={y} width={w} height={h} rx={radius} ry={radius} fill="black" />
        </mask>
      </defs>
      <rect width="100%" height="100%" fill="rgba(0,0,0,0.55)" mask="url(#spotlight-mask)" />
      <rect
        x={x} y={y} width={w} height={h}
        rx={radius} ry={radius}
        fill="none" stroke="rgba(99,102,241,0.6)" strokeWidth="2"
        className="animate-pulse"
      />
    </svg>
  )
}

function TourStep({ step, currentStep, totalSteps, onNext, onPrev, onSkip, onComplete, targetRect }) {
  const Icon = step.icon
  const isFirst = currentStep === 0
  const isLast = currentStep === totalSteps - 1
  const progress = ((currentStep + 1) / totalSteps) * 100

  // Position the modal near the target element, or center if no target
  let modalStyle = {}
  if (targetRect) {
    const viewportW = window.innerWidth
    const viewportH = window.innerHeight
    const modalW = 400
    const modalH = 280
    let left = targetRect.right + 16
    let top = targetRect.top

    if (left + modalW > viewportW - 16) {
      left = targetRect.left - modalW - 16
    }
    if (left < 16) {
      left = Math.max(16, targetRect.left + (targetRect.width - modalW) / 2)
      top = targetRect.bottom + 16
    }
    if (top + modalH > viewportH - 16) {
      top = viewportH - modalH - 16
    }
    if (top < 16) top = 16

    modalStyle = { position: 'absolute', left, top, zIndex: 2 }
  }

  const isCenter = !targetRect

  return (
    <div className="fixed inset-0 z-[100]">
      {isCenter ? (
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      ) : (
        <SpotlightOverlay targetRect={targetRect} />
      )}
      
      <div
        className={isCenter ? 'absolute inset-0 flex items-center justify-center p-4' : ''}
        style={!isCenter ? { position: 'absolute', inset: 0, pointerEvents: 'none' } : undefined}
      >
        <div
          className="bg-white dark:bg-surface-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-fade-in border border-transparent dark:border-surface-700"
          style={{ ...modalStyle, pointerEvents: 'auto', maxWidth: 400 }}
        >
          <div className="h-1 bg-surface-100 dark:bg-surface-700">
            <div 
              className="h-full bg-gradient-to-r from-brand-500 to-brand-600 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          
          <div className="p-6 pb-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-brand-100 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400">
                  <Icon className="w-6 h-6" />
                </div>
                <span className="text-sm text-surface-500 dark:text-surface-400">
                  Step {currentStep + 1} of {totalSteps}
                </span>
              </div>
              <button
                onClick={onSkip}
                className="text-surface-400 dark:text-surface-500 hover:text-surface-600 dark:hover:text-surface-300 transition-colors"
                aria-label="Close tour"
              >
                <HiX className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          <div className="px-6 pb-6">
            <h2 className="text-xl font-bold text-surface-900 dark:text-surface-100 mb-3">
              {step.title}
            </h2>
            <p className="text-surface-600 dark:text-surface-400 leading-relaxed">
              {step.content}
            </p>
          </div>
          
          <div className="px-6 pb-6 flex items-center justify-between gap-3">
            <button
              onClick={onSkip}
              className="text-sm text-surface-500 dark:text-surface-400 hover:text-surface-700 dark:hover:text-surface-300 transition-colors"
            >
              Skip tour
            </button>
            
            <div className="flex items-center gap-2">
              {!isFirst && (
                <button onClick={onPrev} className="btn btn-secondary px-4 py-2">
                  <HiArrowLeft className="w-4 h-4 mr-1" />
                  Back
                </button>
              )}
              
              {isLast ? (
                <button onClick={onComplete} className="btn btn-primary px-6 py-2">
                  Get Started
                  <HiCheckCircle className="w-4 h-4 ml-2" />
                </button>
              ) : (
                <button onClick={onNext} className="btn btn-primary px-4 py-2">
                  Next
                  <HiArrowRight className="w-4 h-4 ml-1" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function OnboardingTour({ onComplete }) {
  const [currentStep, setCurrentStep] = useState(0)
  const [isVisible, setIsVisible] = useState(true)
  const [targetRect, setTargetRect] = useState(null)

  const updateTargetRect = useCallback(() => {
    const step = TOUR_STEPS[currentStep]
    if (step.highlight) {
      const el = document.querySelector(step.highlight)
      if (el) {
        setTargetRect(el.getBoundingClientRect())
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        return
      }
    }
    setTargetRect(null)
  }, [currentStep])

  useEffect(() => {
    // Small delay to let DOM settle after step change
    const timer = setTimeout(updateTargetRect, 100)
    window.addEventListener('resize', updateTargetRect)
    window.addEventListener('scroll', updateTargetRect, true)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', updateTargetRect)
      window.removeEventListener('scroll', updateTargetRect, true)
    }
  }, [updateTargetRect])

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1)
    }
  }

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1)
    }
  }

  const handleSkip = () => {
    setIsVisible(false)
    onComplete?.()
  }

  const handleComplete = () => {
    setIsVisible(false)
    onComplete?.()
  }

  if (!isVisible) return null

  return createPortal(
    <TourStep
      step={TOUR_STEPS[currentStep]}
      currentStep={currentStep}
      totalSteps={TOUR_STEPS.length}
      onNext={handleNext}
      onPrev={handlePrev}
      onSkip={handleSkip}
      onComplete={handleComplete}
      targetRect={targetRect}
    />,
    document.body
  )
}
